import { authorizedAxiosInstance } from '@/clients';
import { useUserStore } from '@/stores/database/entities/00-RBAC/01-Users/store';
import type { UserPlan } from '@/types';

interface BillingScope {
    workspaceId: string;
    userId: string;
}

const getBillingScope = (): BillingScope => {
    const user = useUserStore.getState().selectedUser;
    return { workspaceId: user?.activeWorkspace?.id ?? '', userId: user?.id ?? '' };
};

const billingScopeKey = ({ workspaceId, userId }: BillingScope): string => `${workspaceId}:${userId}`;

const assertCurrentBillingScope = (scope: BillingScope): void => {
    if (!scope.workspaceId || !scope.userId || billingScopeKey(scope) !== billingScopeKey(getBillingScope())) {
        throw new Error('The active workspace changed. Reopen billing and try again.');
    }
};

export async function createBillingPortalSession(redirect: string, plan?: UserPlan): Promise<string> {
    const scope = getBillingScope();
    assertCurrentBillingScope(scope);
    const response = await authorizedAxiosInstance.post<{ url: string }>(
        '/api/billing/billing-portal',
        { redirect, plan },
        {
            headers: { 'X-Billing-Workspace-Id': scope.workspaceId },
        },
    );
    assertCurrentBillingScope(scope);
    const url = new URL(response.data.url);
    if (url.protocol !== 'https:' || url.hostname !== 'billing.stripe.com' || url.username || url.password) {
        throw new Error('The billing portal returned an invalid destination.');
    }
    return url.toString();
}
